import React, {useEffect, useRef, useState} from 'react';
import {Text, Pressable, ScrollView} from 'react-native';
import {ObjectId, Decimal128, UUID} from 'bson';
import Colors from '../../Styles/Shared/Colors';
import {Icons} from '../../Constants/Icons';
import CustomPressable from '../Shared/CustomPressable';
import {usePowerSyncData} from '../../Providers/PowerSyncDataProvider';
import {Navigation} from '../../Constants/Navigation';
import {TemplateCategoryType} from '../../Constants/TemplateCategoryTypes';
import {
  Submission,
  SubmissionStatus,
  Template,
} from '../../Models/PowerSyncModels/models';
import {CommonStyleSheet} from '../../Styles/Shared/CommonStyles';
import {ReportsStyleSheet} from '../../Styles/ReportsStyles';
import {CATEGORY, PERMISSIONS} from '../../Constants/AppConstants';
import {usePermission} from '../../Providers/PermissionProvider';
import ConfirmModal from './ConfirmModal';
import usePowerSyncWriteData from '../../Hooks/usePowerSyncWriteData';
import {usePowerSyncUser} from '../../Providers/PowerSyncUserProvider';
import {userInfo} from 'os';
import { IGeneralHelperService } from '../../Services/Interfaces/IGeneralHelperService';
import GeneralHelperService from '../../Services/GeneralHelperService';

type FormListProps = {
  templateCategoryName: TemplateCategoryType;
  navigation: any;
  category: string;
};

const FormList = (props: FormListProps): React.ReactElement => {
  const {templates, templateVersions, submissionStatuses, powersyncReady} =
    usePowerSyncData();

  const user = usePowerSyncUser();

  const {updateSubmission, createSubmission} = usePowerSyncWriteData();

  const [filteredTemplates, setFilteredTemplates] = useState<Template[]>([]);
  const {hasPermission} = usePermission();
  const [canCreate, setCanCreate] = useState<boolean>();
  const helperService: IGeneralHelperService = new GeneralHelperService();

  const createModalCommand = useRef({
    openModal: () => {},
    setOpenModal(func: () => void) {
      this.openModal = func;
    },
  });

  useEffect(() => {
    let createReport = false;
    if (props.category == CATEGORY.REPORTS) {
      createReport = hasPermission(PERMISSIONS.REPORT_CREATE);
    } else if (props.category == CATEGORY.EQUIPMENT) {
      createReport = hasPermission(PERMISSIONS.REPORT_EQUIPMENT_CREATE);
    } else if (props.category == CATEGORY.OPERATIONS) {
      createReport = hasPermission(PERMISSIONS.REPORT_OPERATION_CREATE);
    } else if (props.category == CATEGORY.PEOPLE) {
      createReport = hasPermission(PERMISSIONS.REPORT_PEOPLE_CREATE);
    }
    setCanCreate(createReport);
  }, [powersyncReady]);

  useEffect(() => {
    const fetchTemplates = async () => {
      const filteredTemplates = templates.filter(
        x =>
          x.templateCategory.templateCategoryName == props.templateCategoryName,
      );

      setFilteredTemplates(filteredTemplates);
    };

    fetchTemplates();
  }, [powersyncReady]);

  const onPress = async (templateId: string) => {
    if (!canCreate) {
      createModalCommand.current.openModal();
      return;
    }

    let filteredTemplates = templateVersions
      .filter(x => x.templateId === templateId)
      .sort((a, b) => b.version - a.version)
      .map(x => ({_id: x._id, name: x.name}));

    if (filteredTemplates.length == 0) {
      //throw Error('No forms found!');
      console.error('No forms found!');
      return;
    }

    let newDraftId = new ObjectId().toString();

    const draftStatus: SubmissionStatus = submissionStatuses.find(
      x => x.name == 'Draft',
    )!;

    let currentUser = user.getUserInfo();
    let org = '';
    let currentEmail = '';

    if (currentUser) {
      org = currentUser.orgId.toString();
      currentEmail = currentUser.email;
    }

    let newTemplateVersion = filteredTemplates[0];

    let newTemplate = templates.find(x => x._id === templateId!);

    let newSubmission: Submission = {
      _id: newDraftId,
      partition: org,
      createdBy: currentEmail as string,
      updatedBy: currentEmail as string,
      templateVersion: {
        _id: newTemplateVersion!._id,
        name: newTemplateVersion!.name,
      },
      templateType: {
        _id: newTemplate!.templateType._id,
        name: newTemplate!.templateType.name,
      },
      submissionStatus: {
        _id: draftStatus._id,
        name: draftStatus.name,
      },
      SQLServerId: new UUID().toString().toUpperCase(),
      answers: [],
      metadataJSON: '',
      createDateTimeStamp: new Date(),
      updateDateTimeStamp: new Date(),
      attachmentsUploaded: true,
    };

    await createSubmission(newSubmission);

    if (newDraftId == null) throw Error('Error: create draft failed!');

    props.navigation.navigate(Navigation.DYNAMICFORM, {
      submissionId: newDraftId,
      submissionStatus: 'Draft',
    });
  };

  const forms: React.ReactElement[] = [];

  filteredTemplates.map((x, index) => {
    let svgxml = '';

    if (x.iconSvg) svgxml = helperService.getTemplateIcon(x.iconSvg, Colors.white, Colors.green);

    forms.push(
      <CustomPressable
        key={index}
        style={({pressed}) => [
          ReportsStyleSheet.fabItem,
          pressed && {
            backgroundColor: Colors.teal,
          },
          !canCreate && {
            opacity: 0.4,
          },
        ]}
        onPress={() => {
          onPress(x._id);
        }}
        tooltip={x.name.replace('Full - ', '').replace('/', ' / ') + ' Report'}>
        {x.iconSvg && (
          <img
            src={`data:image/svg+xml;utf8,${encodeURIComponent(svgxml)}`}
            style={ReportsStyleSheet.fabIcon}
            alt='React Logo'
          />
        )}
      </CustomPressable>,
    );
  });

  /*  Hidden For now
  forms.push(

    <Pressable
      key={"other"}
      style={[ReportsStyleSheet.fabItem, { marginRight: 20, display: "none" }]}
      android_ripple={fabRipple}
    >
      <SvgXml xml={Icons.MORE} style={ReportsStyleSheet.fabIcon} />
    </Pressable>
  ); */

  return (
    <>
      <ConfirmModal
        title='No Access'
        message='You do not have access to create reports'
        okClick={() => {}}
        okText='Ok'
        setOpenModal={(func: () => void) => {
          createModalCommand.current.setOpenModal(func);
        }}
      />
      {forms}
    </>
  );
};

export default FormList;
